import React from 'react';
import { translate } from '../../../services/translate';
import { NavigationProp, RouteProp, useNavigation } from '@react-navigation/native';
import { CRMParamList } from '../../../typings/Params';
import FilterModal from '../../../components/ModernFilters/FilterModal';
import RadioList from '../../../components/ModernFilters/RadioList';
import { useForm } from 'react-hook-form';
import PeriodSelector from '../../../components/ModernFilters/PeriodSelector';
import NumericRange from '../../../components/ModernFilters/NumericRange';
import RegionsList from '../../../components/ModernFilters/RegionsList';
import CompanyList from '../../../components/ModernFilters/crm/CompanyList';
import { useCurrentApp } from '../../../context/CurrentApp';

interface Props {
    filters: CRMParamList['MaintenanceRequests'];
    setFilters: React.Dispatch<React.SetStateAction<CRMParamList['MaintenanceRequests']>>;
    authenticatedUser?: AuthenticatedUser | null;
}

export default function Filter({ filters, setFilters }: Props) {
    const navigation = useNavigation<NavigationProp<RouteProp<CRMParamList, 'MaintenanceRequests'>>>();
    const { isParentApp } = useCurrentApp();

    type FiltersForm = {
        companyId: number | null;
        status: 'onlyOffline' | 'onlyClosed' | null;
        start: {
            from: Date | null;
            to: Date | null;
        };
        finish: {
            from: Date | null;
            to: Date | null;
        };
        duration: {
            from: number | null;
            to: number | null;
        };
        polygon: {
            type: string;
            value: number;
        };
    };

    const form = useForm<FiltersForm>({
        mode: 'onChange',
        defaultValues: {
            companyId: filters.companyId ?? null,
            status: filters.onlyOffline ? 'onlyOffline' : (filters.onlyClosed ? 'onlyClosed' : null),
            start: {
                from: filters.fromStart ? new Date(filters.fromStart) : null,
                to: filters.toStart ? new Date(filters.toStart) : null,
            },
            finish: {
                from: filters.fromFinish ? new Date(filters.fromFinish) : null,
                to: filters.toFinish ? new Date(filters.toFinish) : null,
            },
            duration: {
                from: filters.fromDuration,
                to: filters.toDuration,
            },
            polygon: {
                type: filters.polygonType ?? '',
                value: filters.polygon ?? 0,
            }
        },
    });

    function handleFilter() {

        const formValues = form.getValues();

        const params = {
            companyId: formValues.companyId ?? undefined,
            fromDuration: formValues.duration.from ?? undefined,
            toDuration: formValues.duration.to ?? undefined,
            onlyOffline: formValues.status === 'onlyOffline',
            onlyClosed: formValues.status === 'onlyClosed',
            fromStart: formValues.start.from?.getTime(),
            toStart: formValues.start.to?.getTime(),
            fromFinish: formValues.finish.from?.getTime(),
            toFinish: formValues.finish.to?.getTime(),
            polygonType: formValues.polygon.type !== '' ? formValues.polygon.type : undefined,
            polygon: formValues.polygon.value != 0 ? formValues.polygon.value : undefined
        };

        navigation.setParams({
            ...filters,
            page: 1,
            ...params,
        });

        setFilters({
            ...filters,
            page: 0,
            ...params,
        });
    }

    const polygon = form.watch('polygon');

    return (
        <FilterModal
            form={form}
            onFilters={handleFilter}
            applyDisabled={polygon.type != '' && polygon.value == 0}
            cleanValues={{
                companyId: null,
                status: null,
                start: {
                    from: null,
                    to: null,
                },
                finish: {
                    from: null,
                    to: null,
                },
                duration: {
                    from: null,
                    to: null,
                },
                polygon: {
                    type: '',
                    value: 0,
                }
            }}
            filters={[{
                label: translate('organizational'),
                filters: [{
                    componentHandlesCount: true,
                    name: 'companyId',
                    label: translate('company'),
                    component: CompanyList,
                    omit: !isParentApp,
                    cleans: ['polygon'],
                }],
            }, {
                label: translate('maintenanceRequestData'),
                filters: [{
                    componentHandlesCount: false,
                    name: 'status',
                    label: translate('status'),
                    getCount: RadioList.getCount,
                    component: ({ name }) => <RadioList name={name} clearValue={null} items={[
                        { label: translate('onlyOpenedRequests'), value: 'onlyOffline' },
                        { label: translate('onlyClosedRequests'), value: 'onlyClosed' },
                    ]} />,
                }, {
                    componentHandlesCount: true,
                    name: 'start',
                    label: translate('startBetween'),
                    component: PeriodSelector,
                }, {
                    componentHandlesCount: true,
                    name: 'finish',
                    label: translate('finishBetween'),
                    component: PeriodSelector,
                }, {
                    componentHandlesCount: true,
                    name: 'duration',
                    component: NumericRange,
                    label: `${translate('duration')} (${translate('minutes')})`,
                }, {
                    componentHandlesCount: false,
                    name: 'polygon',
                    getCount: RegionsList.getCount,
                    component: ({ name }) => <RegionsList
                        name={name}
                        companyId={form.watch('companyId')}
                    />,
                    label: translate('region'),
                }],
            }]}
        />
    );
}
